import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import LogbookTodayCanvas from "./LogbookTodayCanvas";

const MANAGER_ROLES = ["rightwork_internal", "company_admin", "company_manager"];

// Daily -> All locations: every eligible store's canvas for one date,
// stacked in the order the daily logbook email uses. Each section is a full
// LogbookTodayCanvas bound to its own store, so its fetch, entry writes, and
// agent regenerate are store-scoped exactly as on the single-store page.
//
// stores: [{ id, store_name, permission_type }]
export default function LogbookMultiStoreDaily(props) {
  const [collapsed, setCollapsed] = React.useState({});
  const [outstandingByStore, setOutstandingByStore] = React.useState({});

  const { onResponsesOutstandingChange } = props;
  React.useEffect(() => {
    const total = props.stores.reduce(
      (sum, store) =>
        MANAGER_ROLES.includes(store.permission_type)
          ? sum + (outstandingByStore[store.id] || 0)
          : sum,
      0
    );
    onResponsesOutstandingChange?.(total);
  }, [outstandingByStore, props.stores, onResponsesOutstandingChange]);

  const setOutstanding = React.useCallback((storeId, count) => {
    setOutstandingByStore((prev) =>
      prev[storeId] === count ? prev : { ...prev, [storeId]: count }
    );
  }, []);

  return (
    <div className="flex flex-col gap-6">
      {props.stores.map((store) => {
        const isCollapsed = Boolean(collapsed[store.id]);
        // Per-store role: the hub's role is the HOME store's, but a user can
        // manage one location and be staff at another.
        const canManage = MANAGER_ROLES.includes(store.permission_type);
        const outstanding = outstandingByStore[store.id] || 0;
        return (
          <section
            key={store.id}
            data-testid={`logbook-store-section-${store.id}`}
          >
            <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-3">
              <button
                type="button"
                onClick={() =>
                  setCollapsed((prev) => ({ ...prev, [store.id]: !isCollapsed }))
                }
                aria-expanded={!isCollapsed}
                className="flex items-center gap-1 text-base font-semibold text-slate-900"
              >
                {isCollapsed ? (
                  <ChevronRightIcon style={{ fontSize: "20px" }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: "20px" }} />
                )}
                {store.store_name}
              </button>
              {outstanding > 0 && canManage && (
                <span className="text-xs font-semibold text-rose-700 bg-rose-50 rounded-lg px-3 py-1.5 whitespace-nowrap">
                  {outstanding} {outstanding === 1 ? "response" : "responses"}{" "}
                  required
                </span>
              )}
            </div>
            {/* Hidden, not unmounted: collapsing keeps the loaded page and
                its outstanding count instead of refetching on expand. */}
            <div className={isCollapsed ? "hidden" : ""}>
              <LogbookTodayCanvas
                date={props.date}
                storeId={store.id}
                homeCompany={props.homeCompany}
                opsRefreshSignal={props.opsRefreshSignal}
                canManage={canManage}
                onResponsesOutstandingChange={(count) =>
                  setOutstanding(store.id, count)
                }
              />
            </div>
          </section>
        );
      })}
    </div>
  );
}
